import React, { Component } from 'react';
import PropTypes from 'prop-types';

import get from 'utils/get';
import slugify from 'utils/slugify';
import flattenMediaData from 'utils/flattenMediaData';
import contentfulImgUtil from 'utils/contentfulImgUtil';
import imageModel from 'models/imageModel';
import cx from 'classnames';
import marked from 'marked';
import { Orientations } from 'constants/Variables';

import { ImageCarousel, Image } from 'components/base';
import CaptionImageBlock from 'components/CaptionImageBlock';

class BlockDoubleImage extends Component {
  state = {
    imagesLoaded: false
  };

  imagesToLoad = [];

  imageMounted = key => {
    this.imagesToLoad.push({ key, loaded: false });
  };

  imageLoaded = key => {
    if (this.imagesToLoad[key]) {
      this.imagesToLoad[key].loaded = true;
    }
    if (this.imagesToLoad.every(image => image.loaded)) {
      this.setState({ imagesLoaded: true });
    }
  };

  render() {
    const { block } = this.props;
    const fields = get(block, 'fields', {});
    const title = get(fields, 'title', '');
    const image1Fields = get(fields, 'image1.fields', {});
    const image1PositionY = get(fields, 'image1PositionY', 0);
    const image1PositionX = get(fields, 'image1PositionX', 0);
    const image2Fields = get(fields, 'image2.fields', {});
    const image2PositionY = get(fields, 'image2PositionY', 0);
    const image2PositionX = get(fields, 'image2PositionX', 0);
    const paddingTop = slugify(get(fields, 'paddingTop', 'Large'));
    const paddingBottom = slugify(get(fields, 'paddingBottom', 'Large'));
    const imageAlign = slugify(get(fields, 'imageAlign', 'Horizontal'));
    const blockAlign = slugify(get(fields, 'blockAlign', 'Center'));
    const showBlockTitle = get(fields, 'showBlockTitle', false);
    const image1Size = slugify(get(image1Fields, 'imageSize', 'none'));
    const image2Size = slugify(get(image2Fields, 'imageSize', 'none'));
    const image1SizeByHeight = get(image1Fields, 'imageSizeByHeight', '');
    const image2SizeByHeight = get(image2Fields, 'imageSizeByHeight', '');
    const image1TextPosition = slugify(get(image1Fields, 'textPosition', ''));
    const image2TextPosition = slugify(get(image2Fields, 'textPosition', ''));
    const image1Text = get(image1Fields, 'text', '');
    const image2Text = get(image2Fields, 'text', '');

    return (
      <div
        className={cx(
          `relative padding-top-${paddingTop} padding-bottom-${paddingBottom} block-width mxauto flex items-center justify-center`,
          { guideline: showBlockTitle }
        )}
      >
        {showBlockTitle ? (
          <p className="block-title absolute r0 t0">{title}</p>
        ) : null}
        <ImageCarousel
          className="mobile-carousel"
          imagesLoaded={this.state.imagesLoaded}
          waitForImagesToLoad
        >
          {[image1Fields, image2Fields].map((imageBlock, i) => {
            const image = flattenMediaData(get(imageBlock, 'image', {}));
            const textAlign = slugify(get(imageBlock, 'textAlign', 'Start'));

            if (!!image.url) {
              const text = get(imageBlock, 'text', '');
              const textWidthIsSmall = get(imageBlock, 'textWidthSmall', false);
              return (
                <div
                  className={cx('carousel-container flex flex-col', {
                    'items-start': textAlign === Orientations.START,
                    'items-center': textAlign === Orientations.CENTER,
                    'items-end': textAlign === Orientations.END
                  })}
                  key={get(image, 'title', i)}
                >
                  <div className="w100 px_5 md:px1">
                    <Image
                      onMount={() => this.imageMounted(i)}
                      onImgLoad={() => this.imageLoaded(i)}
                      className="w100 fit-contain"
                      src={contentfulImgUtil(image.url, '500')}
                      alt={get(image, 'description', '')}
                    />
                  </div>
                  <div
                    className={cx(
                      'caption caption-text-width markdown-text p1',
                      {
                        'caption-text-width-small': textWidthIsSmall
                      }
                    )}
                    dangerouslySetInnerHTML={{
                      __html: marked(text)
                    }}
                  />
                </div>
              );
            }

            return null;
          })}
        </ImageCarousel>
        <div
          className={cx(`w100 none lg:flex`, {
            [`flex-row items-start justify-${blockAlign}`]:
              imageAlign === Orientations.HORIZONTAL ||
              imageAlign === Orientations.OVERLAY,
            [`flex-col items-${blockAlign} justify-center`]:
              imageAlign === Orientations.VERTICAL
          })}
        >
          <div
            style={{
              transform: `translate(${image1PositionX}%, ${image1PositionY}%)`
            }}
            className={cx(`z1 none lg:block image-width-${image1Size}`, {
              [`image-width-${image1Size}-container`]:
                !image1SizeByHeight &&
                image1TextPosition !== 'bottom' &&
                image1Text,
              [`image-width-${image1Size}`]: !!image1Fields.image
            })}
          >
            <CaptionImageBlock block={image1Fields} />
          </div>
          <div
            style={{
              transform: `translate(${image2PositionX}%, ${image2PositionY}%)`
            }}
            className={cx(`z2 none lg:block`, {
              [`image-width-${image2Size}-container`]:
                !image2SizeByHeight &&
                image2TextPosition !== 'bottom' &&
                !!image2Text,
              [`image-width-${image2Size}`]: image2Fields.image
            })}
          >
            <CaptionImageBlock block={image2Fields} />
          </div>
        </div>
      </div>
    );
  }
}

BlockDoubleImage.propTypes = {
  block: PropTypes.shape({
    fields: PropTypes.shape({
      image1: imageModel.propTypes,
      image1PositionX: PropTypes.number,
      image1PositionY: PropTypes.number,
      image2: imageModel.propTypes,
      image2PositionX: PropTypes.number,
      image2PositionY: PropTypes.number,
      paddingTop: PropTypes.string,
      paddingBottom: PropTypes.string,
      title: PropTypes.string,
      blockAlign: PropTypes.string,
      imageAlign: PropTypes.string,
      showBlockTitle: PropTypes.bool
    })
  })
};

BlockDoubleImage.defaultProps = {
  block: {
    fields: {
      image1: imageModel.default,
      image1PositionX: 0,
      image1PositionY: 0,
      image2: imageModel.default,
      image2PositionX: 0,
      image2PositionY: 0,
      paddingTop: 'Large',
      paddingBottom: 'Large',
      title: '',
      blockAlign: 'Center',
      imageAlign: 'Horizontal',
      showBlockTitle: false
    }
  }
};

export default BlockDoubleImage;
